import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  useLayoutEffect,
} from 'react'
import { SvgAccordionArrow } from '../../assets/images'
import { noop } from 'lodash'
import useEffectAfterFirstRender from '../hooks/useEffectAfterFirstRender'
import { ShortcutKeys } from '../../utils/tab-indexing-contants'
export interface StickyHeaderAccordionPropType {
  heading: ReactNode
  subHeading: ReactNode
  isDefaultActive?: boolean
  children: React.ReactNode
  className?: string
  headerClassName?: string
  contentClassName?: string
  isDisabled?: boolean
  triggerOpen?: boolean
  trigger?: { isOpen: boolean }
  borderColor?: string
  alwaysOpen?: boolean
  accordianHeightPadding?: string
  handleStickyHeaderAccordionClick?: (accordionExpanded: boolean) => void
  isConditional?: boolean
  id?: string
  tabIndex?: number
}
const StickyHeaderAccordion: React.FC<StickyHeaderAccordionPropType> = ({
  heading,
  subHeading,
  isDefaultActive = false,
  children,
  className = '',
  headerClassName = '',
  contentClassName = '',
  isDisabled = false,
  triggerOpen = false,
  trigger,
  borderColor = 'secondary-450',
  alwaysOpen = false,
  accordianHeightPadding = 'px-5 h-10',
  handleStickyHeaderAccordionClick,
  isConditional = false,
  id,
  tabIndex,
}) => {
  const [accordionExpanded, setStickyHeaderAccordionExpanded] =
    useState<boolean>(
      isDisabled ? false : isDefaultActive || trigger?.isOpen === true
    )
  const contentRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (contentRef?.current) {
      const isOpen = !isDisabled && (accordionExpanded || alwaysOpen)
      contentRef.current.style.maxHeight = isOpen
        ? `${contentRef.current.scrollHeight}px`
        : '0px'
      setTimeout(() => {
        if (contentRef?.current) {
          contentRef.current.style.maxHeight = isOpen ? `100%` : '0px'
          contentRef.current.style.height = isOpen ? `100%` : '0px'
        }
      }, 250)
      contentRef.current.style.display = isOpen ? 'block' : 'none'
    }
  }, [accordionExpanded])
  useEffect(() => {
    if (triggerOpen) {
      setStickyHeaderAccordionExpanded(true)
    }
  }, [triggerOpen])
  useEffect(() => {
    if (trigger) {
      if (trigger.isOpen) setStickyHeaderAccordionExpanded(true)
      else setStickyHeaderAccordionExpanded(false)
    }
  }, [trigger])
  const handleToggle = () => {
    setStickyHeaderAccordionExpanded((prev) => !prev)
  }
  useEffectAfterFirstRender(() => {
    handleStickyHeaderAccordionClick &&
      handleStickyHeaderAccordionClick(accordionExpanded)
  }, [accordionExpanded])
  return (
    <>
      <div
        className={`font-primary overflow-hidden border border-accent-200 text-black ${
          accordionExpanded ? 'rounded-t' : 'rounded'
        } h-fit max-h-fit flex-col sticky top-0 flex-shrink-0 ${headerClassName}`}
        id={id}
      >
        <div
          className={`header cursor-pointer items-center flex border-l-4 border-${borderColor} ${className} ${accordianHeightPadding}`}
          onClick={isDisabled || alwaysOpen ? noop : handleToggle}
          style={{
            borderLeftColor: borderColor.startsWith('#') ? borderColor : ``,
          }}
          tabIndex={tabIndex ?? 0}
          onKeyDown={(e) => {
            if (
              (e?.key === ShortcutKeys.Space ||
                e?.key === ShortcutKeys.Enter) &&
              !(isDisabled || alwaysOpen)
            ) {
              e?.preventDefault()
              handleToggle()
            }
          }}
          role="button"
          aria-expanded={accordionExpanded}
          aria-disabled={isDisabled}
        >
          <div className="flex-1 text-xs font-medium mr-3">{heading}</div>
          <div>
            <SvgAccordionArrow
              className={`transform transition-transform duration-200  ${
                accordionExpanded ? 'rotate-180' : ''
              } ${isDisabled || alwaysOpen ? 'invisible' : ''}`}
              id={id}
            />
          </div>
        </div>
        {isConditional ? (
          <div>{accordionExpanded ? subHeading : null}</div>
        ) : (
          <div>{subHeading}</div>
        )}
      </div>
      <div className={contentClassName}>
        <div
          className={`transition-all hide-scrollbar duration-350 ease-in-out`}
          ref={contentRef}
        >
          {isConditional ? (
            <div className="h-full hide-scrollbar overflow-auto">
              {accordionExpanded ? children : null}
            </div>
          ) : (
            <div className="h-full hide-scrollbar overflow-auto">
              {children}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
export default StickyHeaderAccordion
